import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListCheckIcon, UserRoundPlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useRoleApi } from '../../services/api/useRole.api';
import { useTeamApi } from '../../services/api/useTeam.api';
import { useUserApi } from '../../services/api/useUser.api';
import { Invitation } from '../../types/Invitation.type';
import { Role } from '../../types/Role/Role.type.ts';
import { Team } from '../../types/Team/Team.type';

const UserInviteDataWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginY: '16px',
});

const DialogTitleWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '36px',
});

function InviteAdminForm() {
  const queryClient = useQueryClient();
  const { getTeams } = useTeamApi();
  const { getRoles } = useRoleApi();
  const { sendInvitations } = useUserApi();

  const [email, setEmail] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<number>();
  const [selectedRole, setSelectedRole] = useState<number>();
  const [dialogVisible, setDialogVisible] = useState(false);

  const isInviteDisabled = !email || !selectedTeam || !selectedRole;

  const openInviteDialog = () => {
    setDialogVisible(true);
  };

  const closeInviteDialog = () => {
    setDialogVisible(false);
    setEmail('');
    setSelectedTeam(undefined);
    setSelectedRole(undefined);
  };

  const {
    data: teamsData,
    fetchNextPage: fetchNextTeams,
    hasNextPage: hasNextTeams,
    isFetchingNextPage: isFetchingNextTeams,
  } = useInfiniteQuery({
    queryKey: ['teams'],
    queryFn: async ({ pageParam = 1 }) => {
      return await getTeams({ page: pageParam, per_page: 40 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const teams = teamsData?.pages?.flatMap((page) => page.data) ?? [];

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const {
    mutate: invitationsMutate,
    isSuccess: isInvitationSent,
    data: invitationResponse,
  } = useMutation({
    mutationKey: ['invitations'],
    mutationFn: sendInvitations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manageUsers'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['activeUsers'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'], exact: false });
    },
  });

  const handleInvite = async () => {
    if (isInviteDisabled) return;

    const inviteData: Invitation = { email: email, role_id: selectedRole, team_id: selectedTeam };

    invitationsMutate([inviteData]);

    openInviteDialog();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
      <TextField
        type='email'
        label='Email'
        variant='outlined'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
      />
      <FormControl>
        <InputLabel id='select-team-label' sx={{ fontSize: '14px' }}>
          Select a team
        </InputLabel>
        <Select
          labelId='select-team-label'
          value={selectedTeam?.toString() || ''}
          label='Select a team'
          defaultValue={''}
          onChange={(e) => setSelectedTeam(Number(e.target.value))}
          MenuProps={{
            slotProps: {
              paper: {
                onScroll: (event: React.UIEvent<HTMLDivElement>) => {
                  const target = event.target as HTMLDivElement;
                  const position = target.scrollTop + target.clientHeight;
                  if (target.scrollHeight - position <= 1) {
                    if (hasNextTeams && !isFetchingNextTeams) {
                      fetchNextTeams();
                    }
                  }
                },
                ref: (node: HTMLDivElement | null) => {
                  if (node) {
                    const hasScroll = node.scrollHeight > node.clientHeight;
                    if (!hasScroll && hasNextTeams && !isFetchingNextTeams) {
                      fetchNextTeams();
                    }
                  }
                },
              },
            },
          }}
        >
          {teams.map((team: Team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel id='select-role-label' sx={{ fontSize: '14px' }}>
          Select a role
        </InputLabel>
        <Select
          labelId='select-role-label'
          value={selectedRole?.toString() || ''}
          label='Select a role'
          defaultValue={''}
          onChange={(e) => setSelectedRole(Number(e.target.value))}
        >
          {roles?.map((role: Role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button variant='contained' disabled={isInviteDisabled} onClick={handleInvite}>
        Invite
      </Button>

      {isInvitationSent && (
        <>
          <Dialog open={dialogVisible} onClose={closeInviteDialog} fullWidth>
            <DialogTitleWrapper>
              <ListCheckIcon size={62} color='#132B50' />
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Invites Sent</Typography>
            </DialogTitleWrapper>
            <DialogContent>
              {invitationResponse.created_users.length > 0 &&
                invitationResponse.created_users.map((user: { email: string }) => (
                  <UserInviteDataWrapper key={user.email}>
                    <UserRoundPlusIcon size={'20px'} />
                    <Typography sx={{ fontWeight: 'medium' }} variant='body2'>
                      {user.email}
                    </Typography>
                    <Chip sx={{ marginLeft: 'auto', fontSize: '10px' }} label='Invited' color='success' size='small' />
                  </UserInviteDataWrapper>
                ))}

              {invitationResponse.failed_creation_users.length > 0 &&
                invitationResponse.failed_creation_users.map((user: { email: string; error_message: string }) => (
                  <UserInviteDataWrapper key={user.email}>
                    <UserRoundPlusIcon size={'20px'} />
                    <Typography sx={{ fontWeight: 'medium' }} variant='body2'>
                      {user.email}
                    </Typography>
                    <Chip sx={{ marginLeft: 'auto', fontSize: '10px' }} label='Failed' color='error' size='small' />
                  </UserInviteDataWrapper>
                ))}
            </DialogContent>
            <DialogActions>
              <Button sx={{ color: '#000' }} onClick={closeInviteDialog}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default InviteAdminForm;
